import { useMemo } from 'react';
import * as QueryString from 'query-string';
import { TimeRange } from '../types/TimeRange';
import { PERIOD_CUSTOM, PERIOD_ONE_HOUR, periodToValidIntervals } from '../component/Constants';
import { normalizeTz, resolveUserTimezone, unwrapQueryParam, parseUrlTime, nowInTimezone } from '../helpers/timeRangeUtils';

/**
 * Compute the initial TimeRange from the current URL search string and localStorage.
 * This replaces the module-level side effects that previously lived in TimeRangeContext.ts.
 *
 * @param search - the URL search string (from useLocation().search or window.location.search)
 */
export function computeInitialTimeRange(search: string): TimeRange {
    const queryString = QueryString.parse(search);

    const timeZoneValue = resolveUserTimezone();
    const timeNow = nowInTimezone(timeZoneValue);
    const savedInterval = JSON.parse(window.localStorage.getItem('dbmar-interval') || '0');
    const validInterval = periodToValidIntervals.hours_1.includes(savedInterval);

    const timeRange: TimeRange = {
        interval: validInterval ? savedInterval : 0,
        timezone: timeZoneValue,
        previousFrom: timeNow.subtract(2, 'hour').second(0),
        from: timeNow.subtract(1, 'hour').second(0),
        to: timeNow.second(0),
        dateFormat: 'YYYY-MM-DD+HH:mm:00',
        period: PERIOD_ONE_HOUR,
        autorefresh: false,
    };

    if (queryString.fm && queryString.to && queryString.tz) {
        const timezone = unwrapQueryParam(queryString.tz, 'UTC');
        const intervalParam = unwrapQueryParam(queryString.interval, '0');
        const fromTime = parseUrlTime(queryString.fm.toString(), timezone);
        const toTime = parseUrlTime(queryString.to.toString(), timezone);

        if (fromTime && toTime) {
            timeRange.from = fromTime;
            timeRange.to = toTime;
            timeRange.previousFrom = fromTime.subtract(toTime.diff(fromTime, 'millisecond'), 'millisecond');
            timeRange.period = PERIOD_CUSTOM;
            timeRange.interval = parseInt(String(intervalParam), 10) || 0;
            timeRange.timezone = normalizeTz(unwrapQueryParam(queryString.tz, timeZoneValue));
        }
    }

    return timeRange;
}

/**
 * Hook that computes the initial time range once (on first render).
 * Uses useMemo with [] so it only runs once per component mount.
 */
export function useInitialTimeRange(search: string): TimeRange {
    return useMemo(() => computeInitialTimeRange(search), []); // eslint-disable-line react-hooks/exhaustive-deps
}
